import React, { useMemo, useCallback } from 'react';
import '@wix/ricos/css/ricos-viewer.global.css';
import '@wix/ricos/css/all-plugins-viewer.css';
import '@wix/ricos/css/plugin-audio-viewer.global.css';
import '@wix/ricos/css/plugin-button-viewer.global.css';
import '@wix/ricos/css/plugin-collapsible-list-viewer.global.css';
import '@wix/ricos/css/plugin-divider-viewer.global.css';
import '@wix/ricos/css/plugin-file-upload-viewer.global.css';
import '@wix/ricos/css/plugin-gallery-viewer.global.css';
import '@wix/ricos/css/plugin-giphy-viewer.global.css';
import '@wix/ricos/css/plugin-hashtag-viewer.global.css';
import '@wix/ricos/css/plugin-html-viewer.global.css';
import '@wix/ricos/css/plugin-image-viewer.global.css';
import '@wix/ricos/css/plugin-link-viewer.global.css';
import '@wix/ricos/css/plugin-link-preview-viewer.global.css';
import '@wix/ricos/css/plugin-mentions-viewer.global.css';
import '@wix/ricos/css/plugin-social-polls-viewer.global.css';
import '@wix/ricos/css/plugin-spoiler-viewer.global.css';
import '@wix/ricos/css/plugin-table-viewer.global.css';
import '@wix/ricos/css/plugin-vertical-embed-viewer.global.css';
import '@wix/ricos/css/plugin-video-viewer.global.css';

import {
  RicosViewer,
  ensureRicosContent,
  ensureDraftContent,
  fromDraft,
  toDraft,
  pluginCodeBlockViewer,
  pluginHashtagViewer,
  pluginIndentViewer,
  pluginLineSpacingViewer,
  pluginSpoilerViewer,
  pluginTextColorViewer,
  pluginTextHighlightViewer,
  pluginEmojiViewer,
} from '@wix/ricos';
import {
  pluginActionButtonLoadable,
  pluginAudioLoadable,
  pluginCollapsibleListLoadable,
  pluginDividerLoadable,
  pluginFileUploadLoadable,
  pluginGalleryLoadable,
  pluginGiphyLoadable,
  pluginHtmlLoadable,
  pluginImageLoadable,
  pluginLinkButtonLoadable,
  pluginLinkLoadable,
  pluginLinkPreviewLoadable,
  pluginMentionsLoadable,
  pluginPollLoadable,
  pluginTableLoadable,
  pluginVerticalEmbedLoadable,
  pluginVideoLoadable,
} from '@wix/ricos/loadable';
import type { FileUploadData } from '@wix/ricos/dist/src/plugin-file-upload/types';

import type { RichContent } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { DraftJSObject } from '../../../../types/RichContent';
import { st, classes } from '../QuestionsWrapper/styles.st.css';
import { useRCVInstance, useRCVSettings } from './RCVSettingsContext';
import { getDomain, getMediaFileType } from './getMediaFileType';

enum PlatformType {
  Livesite = 'Livesite',
  Editor = 'Editor',
}
interface AudioSrc {
  id: string;
}
const plugins = [
  pluginActionButtonLoadable(),
  pluginAudioLoadable({
    getAudioUrl: (src: AudioSrc) => `https://static.wixstatic.com/${src.id}`,
  }),
  pluginCodeBlockViewer(),
  pluginCollapsibleListLoadable(),
  pluginDividerLoadable(),
  pluginEmojiViewer(),
  pluginGiphyLoadable(),
  pluginHashtagViewer(),
  pluginHtmlLoadable({
    siteDomain: 'https://nivina3.wixsite.com/my-site-334',
  }),
  pluginImageLoadable({ disableTransition: true }),
  pluginIndentViewer(),
  pluginLineSpacingViewer(),
  pluginLinkButtonLoadable(),
  pluginLinkPreviewLoadable(),
  pluginLinkLoadable(),
  pluginMentionsLoadable(),
  pluginPollLoadable(),
  pluginSpoilerViewer(),
  pluginTableLoadable(),
  pluginTextColorViewer(),
  pluginTextHighlightViewer(),
  pluginVerticalEmbedLoadable(),
  pluginVideoLoadable(),
];
const idContainsPrefix = (pathOrId: string) => pathOrId.includes('/');

function resolveFileUrl(
  metaSiteId: string,
  file: FileUploadData,
): Promise<string> {
  const pathOrId = file.path ?? file.id ?? '';
  const fileType = file.type ?? file.mimeType ?? '';
  const name = file.name ?? '';

  if (!pathOrId) {
    return Promise.resolve('');
  }
  const dn = `?dn=${encodeURIComponent(name)}`;
  if (!metaSiteId) {
    return Promise.resolve(`https://wix.usrfiles.com/${pathOrId}${dn}`);
  }
  try {
    const domain =
      file.privacy !== 'private' && idContainsPrefix(pathOrId)
        ? getDomain(getMediaFileType(fileType || 'document'), metaSiteId)
        : `${metaSiteId}.usrfiles.com`;
    return Promise.resolve(`https://${domain}/${pathOrId}${dn}`);
  } catch {
    return Promise.resolve(
      `https://${metaSiteId}.usrfiles.com/${pathOrId}${dn}`,
    );
  }
}

interface RCVProps {
  convertAnswerToJSON: DraftJSObject | RichContent | string;
  answerQuestionId: string;
}

const minimalDraftContent: DraftJSObject = {
  blocks: [
    {
      key: '',
      text: '',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    },
  ],
  entityMap: {},
  documentStyle: {},
  VERSION: '1.0.0',
  ID: '',
};

const RCVComponent = ({ convertAnswerToJSON, answerQuestionId }: RCVProps) => {
  const { metaSiteId, instance } = useRCVInstance();
  const {
    layout,
    textAlignment,
    isMobile,
    isEditor,
    customStyles: customStylesFromContext,
    palette,
  } = useRCVSettings();
  const content = useMemo(() => {
    if (typeof convertAnswerToJSON === 'string') {
      return ensureRicosContent(minimalDraftContent);
    }
    if (Array.isArray((convertAnswerToJSON as RichContent).nodes)) {
      // Ambassador's RichContent has nodes?: Node[]; ricos requires nodes: Node[].
      // The Array.isArray check above guarantees nodes is defined at runtime.
      return ensureRicosContent(
        convertAnswerToJSON as Parameters<typeof ensureRicosContent>[0],
      );
    }
    return ensureRicosContent(
      toDraft(
        fromDraft(ensureDraftContent(convertAnswerToJSON as DraftJSObject), {
          ignoreUnsupportedValues: true,
        }),
      ),
    );
  }, [convertAnswerToJSON]);

  const direction = textAlignment === 'right' ? 'rtl' : 'ltr';
  const parentClass = st(
    classes.answerWrapper,
    { layout },
    answerQuestionId ? `RCV${answerQuestionId}` : undefined,
  );

  const fileUploadResolver = useCallback(
    (file: FileUploadData) => {
      return resolveFileUrl(metaSiteId ?? '', file);
    },
    [metaSiteId],
  );

  const viewerPlugins = useMemo(
    () => [
      pluginFileUploadLoadable({
        resolveFileUrl: fileUploadResolver,
        downloadTarget: '_blank',
      }),
      pluginGalleryLoadable({
        scopingId: answerQuestionId,
      }),
      ...plugins,
    ],
    [fileUploadResolver, answerQuestionId],
  );

  const biSettings = useMemo(
    () => ({
      consumer: '[wix-labs] faq-ooi',
      usage: 'answer',
      platform: isEditor ? PlatformType.Editor : PlatformType.Livesite,
      postId: answerQuestionId,
      containerId: metaSiteId || answerQuestionId || 'faq-ooi-answer',
    }),
    [isEditor, answerQuestionId, metaSiteId],
  );

  const theme = useMemo(
    () => ({
      parentClass,
      customStyles: customStylesFromContext ?? {},
      palette: palette ?? {},
    }),
    [parentClass, customStylesFromContext, palette],
  );

  return (
    <div
      style={{ direction }}
      className={st(classes.answer, { layout, mobile: isMobile })}
    >
      {typeof convertAnswerToJSON === 'string' ? (
        convertAnswerToJSON
      ) : (
        <RicosViewer
          isMobile={isMobile}
          biSettings={biSettings}
          instance={instance}
          theme={theme}
          textAlignment={textAlignment}
          plugins={viewerPlugins}
          content={content}
        />
      )}
    </div>
  );
};
const RCV = React.memo(RCVComponent);
RCV.displayName = 'FaqOoiRCVAnswer';
export default RCV;
